'use client';

import { useRef, useEffect, useCallback, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ImageIcon, FileText, Video, Loader2 } from 'lucide-react';
import type { AssetRecord, ListAssetsInput } from '@impact/assets';
import { cn } from '@/lib/utils';
import { useInfiniteAssetsList } from '@/lib/queries/assets';

interface AssetGalleryPickerProps {
  filters?: ListAssetsInput;
  selectedAssetId?: string | null;
  className?: string;
  emptyMessage?: string;
  columns?: number;
  onSelect: (asset: AssetRecord) => void;
}

const ROW_HEIGHT = 140; // 96px image + ~44px text/padding
const ROW_GAP = 12;

function getAssetIcon(assetType: AssetRecord['assetType']) {
  if (assetType === 'video') return <Video className="h-6 w-6 text-muted-foreground" />;
  if (assetType === 'document') return <FileText className="h-6 w-6 text-muted-foreground" />;
  return <ImageIcon className="h-6 w-6 text-muted-foreground" />;
}

function useContainerColumns(ref: React.RefObject<HTMLDivElement | null>, fallback: number) {
  const [cols, setCols] = useState(fallback);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const w = el.clientWidth;
      if (w >= 1024) setCols(4);
      else if (w >= 540) setCols(3);
      else setCols(2);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return cols;
}

export function AssetGalleryPicker({
  filters = {},
  selectedAssetId,
  className,
  emptyMessage = 'No assets found for these filters.',
  columns: columnsProp,
  onSelect,
}: AssetGalleryPickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const detectedCols = useContainerColumns(scrollRef, 3);
  const cols = columnsProp ?? detectedCols;

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteAssetsList(
    {
      sortBy: 'createdAt',
      sortOrder: 'desc',
      ...filters,
    },
  );

  const allAssets = data?.pages.flatMap((p) => p.data) ?? [];
  const totalItems = data?.pages[0]?.pagination.total ?? 0;
  const rowCount = Math.ceil(allAssets.length / cols);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT + ROW_GAP,
    overscan: 3,
  });

  const virtualRows = virtualizer.getVirtualItems();

  // Fetch next page when user scrolls near the bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < ROW_HEIGHT * 2) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', className)}>
        {Array.from({ length: 8 }).map((_, idx) => (
          <div key={idx} className="h-[140px] animate-pulse rounded border bg-muted" />
        ))}
      </div>
    );
  }

  if (allAssets.length === 0) {
    return (
      <div className={cn('rounded border p-4 text-sm text-muted-foreground', className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: 400 }}>
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualRows.map((virtualRow) => {
            const startIdx = virtualRow.index * cols;
            const rowAssets = allAssets.slice(startIdx, startIdx + cols);

            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: ROW_HEIGHT,
                  transform: `translateY(${virtualRow.start}px)`,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gap: ROW_GAP,
                }}
              >
                {rowAssets.map((asset) => {
                  const isSelected = selectedAssetId === asset.id;

                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => onSelect(asset)}
                      className={cn(
                        'overflow-hidden rounded-md border text-left transition hover:border-primary/60',
                        isSelected && 'border-primary ring-1 ring-primary',
                      )}
                    >
                      <div className="relative h-24 w-full bg-muted">
                        {asset.assetType === 'image' ? (
                          <img
                            src={asset.cdnUrl}
                            alt={asset.objectKey}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            {getAssetIcon(asset.assetType)}
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 p-2">
                        <p className="truncate text-xs font-medium">
                          {asset.assetMetadata?.originalFilename as string}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{asset.mimeType}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {totalItems > 0 && (
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {allAssets.length} of {totalItems} assets
        </p>
      )}
    </div>
  );
}
